import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { projectNode as projectNodePure } from '../../lib/atlasProjection';
import { AtlasProjectionContext } from './AtlasProjectionContext';
import s from './MapAtlasFrame.module.css';

/**
 * The single element that hosts the biome image/video AND the interactive node
 * overlay, guaranteeing they share one bounding rect. This is the structural
 * fix for the production drift: the old split (full-viewport video sibling +
 * smaller fixed canvas for nodes) cover-fit the art and the nodes into two
 * different rects. Here they cover-fit the SAME rect, so projection is exact at
 * every breakpoint.
 *
 * Collapses the former GameCanvas + VideoBackground. Sizing per breakpoint is in
 * the CSS module (mobile/tablet = full viewport, desktop = 1440×810 rounded
 * card), matching the previous VideoBackground extents.
 *
 * @param {object}  atlas      atlas row: { id, asset_url, asset_type, native_width, native_height, version, ... }.
 *        Render mode (img vs video) comes from atlas.asset_type ('image' | 'video') — ADR-050.
 * @param {React.ReactNode} children   positioned overlay content (nodes, figurine, HUD, badge)
 * @param {?{w:number,h:number}} previewSize  BP-MAP-ATLAS-03 (admin editor only):
 *        render the frame at an EXPLICIT pixel size instead of measuring the
 *        viewport. Projection uses these dims so the admin three-up preview
 *        matches what real-device users see; the caller scales the result down
 *        with a CSS transform. Bypasses the ResizeObserver and the production
 *        position:fixed / desktop-card layout, and suppresses map breadcrumbs
 *        (preview renders are not real sessions). Omit it for production.
 */
export default function MapAtlasFrame({ atlas, children, previewSize = null }) {
  const rootRef = useRef(null);
  const isPreview = !!previewSize;
  const [measured, setMeasured] = useState({ w: 0, h: 0 });
  const prevAtlasId = useRef(null);

  // In preview mode the size is fixed (the editor renders at an explicit
  // breakpoint size and scales it via CSS transform), so we use those dims
  // directly and never measure. Otherwise the frame measures its own rect.
  const size = isPreview ? { w: previewSize.w, h: previewSize.h } : measured;

  useEffect(() => {
    if (isPreview) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    const update = () => {
      const r = el.getBoundingClientRect();
      setMeasured((prev) => (prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, [isPreview]);

  // Sentry breadcrumbs: first load + biome switches. Preview renders are not
  // real navigation, so they never emit breadcrumbs.
  useEffect(() => {
    if (isPreview || !atlas?.id) return;
    if (prevAtlasId.current === null) {
      Sentry.addBreadcrumb({
        category: 'map',
        message: 'atlas.loaded',
        level: 'info',
        data: { atlas_id: atlas.id, version: atlas.version },
      });
    } else if (prevAtlasId.current !== atlas.id) {
      Sentry.addBreadcrumb({
        category: 'map',
        message: 'atlas.switched',
        level: 'info',
        data: { from: prevAtlasId.current, to: atlas.id },
      });
    }
    prevAtlasId.current = atlas.id;
  }, [atlas?.id, atlas?.version, isPreview]);

  const nativeW = atlas?.native_width || 0;
  const nativeH = atlas?.native_height || 0;

  const projectNode = useCallback(
    (srcX, srcY, anchor = 'center') =>
      projectNodePure({
        nativeW,
        nativeH,
        containerW: size.w,
        containerH: size.h,
        srcX,
        srcY,
        anchor,
      }),
    [nativeW, nativeH, size.w, size.h]
  );

  const ctx = useMemo(
    () => ({ atlas, containerW: size.w, containerH: size.h, projectNode }),
    [atlas, size.w, size.h, projectNode]
  );

  // Dev-only calibration drift warning: compare painted natural dims vs atlas.
  const warnIfDrift = (naturalW, naturalH) => {
    if (!import.meta.env.DEV || !atlas) return;
    if (naturalW && naturalH && (naturalW !== nativeW || naturalH !== nativeH)) {
      console.warn(
        `[MapAtlasFrame] calibration drift: atlas '${atlas.id}' native ${nativeW}×${nativeH} ` +
        `but asset is ${naturalW}×${naturalH}. Using atlas dims to avoid layout shift.`
      );
    }
  };

  const hasVideo = atlas?.asset_type === 'video';

  // Cache-bust the asset by atlas version. Uploaded assets are stored at a
  // deterministic path (images/${atlasId}.webp) that OVERWRITES the old bytes, so
  // the URL string is identical before and after a re-upload — React's key never
  // changes and the browser/CDN serves the stale cached bytes. Appending the
  // version (bumped by the DB trigger on every save) makes each save produce a
  // distinct URL → key change → remount → cache miss → fresh bytes. Guard against
  // a missing version so any atlas without one falls back to the plain URL.
  const withVersion = (url) => {
    const v = atlas?.version;
    return url && Number.isInteger(v) && v > 0 ? `${url}?v=${v}` : url;
  };
  const assetSrc = withVersion(atlas?.asset_url);

  // Preview (BP-MAP-ATLAS-03): render at an explicit pixel size inside the
  // admin editor's three-up PreviewPane. The production `.frame` CSS class
  // applies `position:fixed` + a landscape media-query that adds
  // `width:100vw; height:auto; min-height:100dvh` — these fight the explicit
  // preview dimensions and misalign projection even when inline styles try to
  // override them (specificity wins, but the cascade order isn't always
  // predictable across browsers). Safest fix: skip the class entirely in preview
  // mode and use a minimal inline-only style so the frame is always exactly
  // previewSize.w × previewSize.h. overflow:hidden is included so the
  // absolutely-positioned .media / .overlay children clip correctly.
  //
  // Production (no previewSize): apply the `.frame` class so position:fixed,
  // the landscape media query, and all production layout rules take effect.
  // Feed the atlas's native aspect ratio inline so the landscape rule's
  // `height: auto` resolves to width / aspect.
  const frameClassName = isPreview ? undefined : s.frame;
  const frameStyle = isPreview
    ? {
        position: 'relative',
        overflow: 'hidden',
        width: `${previewSize.w}px`,
        height: `${previewSize.h}px`,
        borderRadius: previewSize.w >= 1440 ? '24px' : '0',
      }
    : nativeW && nativeH
      ? { aspectRatio: `${nativeW} / ${nativeH}` }
      : undefined;

  return (
    <div className={frameClassName} ref={rootRef} style={frameStyle}>
      {atlas && (hasVideo ? (
        <video
          key={assetSrc}
          className={s.media}
          autoPlay
          loop
          muted
          playsInline
          src={assetSrc}
          onLoadedMetadata={(e) => warnIfDrift(e.currentTarget.videoWidth, e.currentTarget.videoHeight)}
          aria-hidden="true"
        />
      ) : (
        <img
          key={assetSrc}
          className={s.media}
          src={assetSrc}
          alt=""
          onLoad={(e) => warnIfDrift(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
          aria-hidden="true"
        />
      ))}

      <AtlasProjectionContext.Provider value={ctx}>
        <div className={s.overlay}>{children}</div>
      </AtlasProjectionContext.Provider>
    </div>
  );
}
